import { useState, useEffect, useRef } from "react";
import { ProSidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Box, Divider, IconButton, Typography, useTheme } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import EnergySavingsLeaf from "@mui/icons-material/EnergySavingsLeaf"
import Leaderboard from "@mui/icons-material/Leaderboard"
import SpeedIcon from '@mui/icons-material/Speed';
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import WysiwygIcon from '@mui/icons-material/Wysiwyg';
import GetUser from "../../components/api/GetUser"
import ApartmentIcon from '@mui/icons-material/Apartment';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import ArticleIcon from '@mui/icons-material/Article';
import UpcomingIcon from '@mui/icons-material/Upcoming';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import { useParams } from "react-router-dom";
import { useDate } from "../../components/datepicker/DateContext";
import { useYear } from "../../components/yearpicker/YearContext";
import HandymanIcon from '@mui/icons-material/Handyman';
import NextPlanIcon from '@mui/icons-material/NextPlan';
import BoltIcon from '@mui/icons-material/Bolt';
import animationdata from '../../img/anim-logo.json'
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import Cookies from "js-cookie";
import Engineering from '@mui/icons-material/Engineering'
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import GetSiteRec from "../../components/api/GetSiteRec";
import Lottie from "lottie-web";
import LoginCookieAuth from "../../components/api/LoginCookieAuth";
import SkySparkGrid from "../../components/api/SkySparkGrid";

const Item = ({ title, to, icon, color, selected, setSelected, isCollapsed }) => {

  return (
    <>
      <MenuItem
        active={selected === title}
        style={{
          color: color,
        }}
        onClick={() => setSelected(title)}
        icon={icon}
      >
        <Typography>{title}</Typography>
        <Link to={to} aria-label={`Naar ${title}`} />
      </MenuItem>
    </>
  );
};

const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isCollapsed, setIsCollapsed] = useState();
  const { selectedDate } = useDate();
  const { selectedYear } = useYear();
  const [selected, setSelected] = useState("Hoofdpagina");
  const { siteId } = useParams();
  const navigate = useNavigate()
  const location = useLocation();
  // Store pathname for setting active path
  const pathname = location.pathname
  // Animated logo container
  const container = useRef(null);

  // Menu item whitelist
  const [userTabWhitelist, setUserTabWhitelist] = useState([])
  const [projectTabWhitelist, setProjectTabWhitelist] = useState([])
  const [liveTablePages, setLiveTablePages] = useState([])

  // Change state of sidebar based on window-width
  const UpdateSideBar = () => {
    if (window.innerWidth <= 650) {
      setIsCollapsed(true)
    }
  }

  useEffect(() => {
    UpdateSideBar()
  })

  // Listening for resize
  useEffect(() => {
    const handleResize = () => {
      UpdateSideBar();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  // Fetch username
  useEffect(() => {
    // User tab settings
    LoginCookieAuth(Cookies.get('skyarc-auth-8080')).then(async (authResponse) => {
      if (authResponse) {
        GetUser().then((response) => {
          if (response['data']['rows'].length !== 0) {
            let userSettings = []
            // Combine infSiteModules and infSiteDynamicTablePages into a single string
            userSettings = response.data["rows"][0].infSiteModules.toString() + "," + response.data["rows"][0].infSiteDynamicTablePages
            // Split each setting at comma
            userSettings = userSettings.split(',')
            setUserTabWhitelist(userSettings)
          } 
        })
        // Project tab settings
        GetSiteRec(siteId).then((response) => {
          if (response['data']['rows'].length !== 0) {
            setProjectTabWhitelist(response['data']['rows'][0]['settings'])

            // Check if project has dynamic tables before fetchign tables
            if (response['data']['rows'][0]['settings'].some(item => item['infModuleSettingsKey'].includes('dynamictable/'))) {
              SkySparkGrid(`inf_api_query(@${siteId}, "infGijsReadAllTablePages()")`).then((response) => {
                if (response['data']['rows'].length !== 0) setLiveTablePages(response['data']['rows'])
              })
            } else {
              console.log("Project has no dynamictable/ module enabled")
            }
          }
          
        })
      }
    })
  }, [siteId]);

  // Handle highlighted menu item based on pathname
  useEffect(() => {
    if (pathname === `/projects/${siteId}`) {
      setSelected("Portfolio");
    } else if (pathname === `/projects/${siteId}/ems/profile`) {
      setSelected("Energieprofiel");
    } else if (pathname === `/projects/${siteId}/comfort/building`) {
      setSelected("3D Weergave");
    } else if (pathname === `/projects/${siteId}/ems/benchmark`) {
      setSelected("Benchmark");
    } else if (pathname === `/projects/${siteId}/arcs`) {
      setSelected("Arcs");
    } else if (pathname === `/projects/${siteId}/ems/baseload`) {
      setSelected("Basislast")
    } else if (pathname === `/projects/${siteId}/ems/performancetrend`) {
      setSelected("Prestatietrend")
    } else if (pathname === `/projects/${siteId}/ems/energytempcurve`) {
      setSelected("Stooklijn")
    } else if (pathname === `/projects/${siteId}/comfort/table`) {
      setSelected("Tabel Data")
    } else if (pathname === `/projects/${siteId}/buildingoverview`) {
      setSelected("Gebouw overzicht")
    } else if (pathname === `/projects/${siteId}/comfort/componentlocation`) {
          setSelected("3D Componenten")
    }
    liveTablePages.forEach(page => {
      if(pathname === `/projects/${siteId}/comfort/liveTable/${page['id']['val']}/${page['infPageRefreshTime']['val']}`) {
        setSelected(page['infTablePageName'])
      }
    });
  }, [selected, location.pathname, liveTablePages, pathname, siteId])

  useEffect(() => {
    Lottie.loadAnimation({
      container: container.current,
      renderer: 'svg',
      loop: false,
      autoplay: true,
      animationData: animationdata
    });
  }, [isCollapsed])

  return (
    <Box
      sx={{
        // Sidebar background
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
        },
        // Icon background
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        // Icon color
        "& .pro-icon": {
          color: 'black'
        },
        // Text color
        "& .pro-item-content": {
          color: 'black',
        },
        "& .pro-inner-item": {
          fontSize: '16px'
        },
        // Item hover color
        "& .pro-inner-item:hover": {
          "& .pro-item-content": { color: "#00ac73 !important" },
          "& .pro-icon": { color: '#00ac73' },
          color: "#00ac73 !important",
        },
        // Item active color
        "& .pro-menu-item.active": {
          "WebkitTextFillColor": "#00ac73 !important",
          "& .pro-icon": { color: '#00ac73 !important' },
          color: "#00ac73 !important",
        },
        "& .pro-sidebar .pro-menu > ul > .pro-sub-menu > .pro-inner-list-item": {
          backgroundColor: `${colors.primary[400]} !important`
        },
        // Padding between items
        "& .pro-sidebar .pro-menu > ul > .pro-sub-menu > .pro-inner-list-item > div > ul": {
          paddingTop: '0px',
          paddingBottom: '0px'
        },
        "& .popper-inner": {
          marginTop: '8px',
          marginBottom: '8px',
          background: '#f2f0f0 !important',
        },
        "& .pro-sidebar.collapsed .pro-menu > ul > .pro-menu-item.pro-sub-menu > .pro-inner-list-item": {
          borderRadius: '5px',
          boxShadow: 1,
        },
        "& .pro-item-content > p": {
          fontSize: '16px'
        }
      }}
    >
      <ProSidebar collapsed={isCollapsed} width={240}>
        {/* Logos */}
        {
          isCollapsed ? (
            <Box onClick={() => window.innerWidth <= 650 ? (<></>) : (setIsCollapsed(!isCollapsed))}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                height: '1px',
                marginBottom: '30px',
                mt: '5px',
                cursor: 'pointer'
              }}>
              <img src={process.env.PUBLIC_URL + '/inf-logo.svg'} style={{ userSelect: "none", cursor: "pointer" }} width={45} height={45} alt="inf-logo-small" onClick={() => window.innerWidth <= 650 ? (<></>) : (setIsCollapsed(!isCollapsed))} />
            </Box>
          ) : (
            undefined
          )
        }
        <Menu iconShape="square">
          {!isCollapsed && (
            <Box sx={{ display: 'flex', padding: '8px', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginTop: '-6px' }} onClick={() => setIsCollapsed(!isCollapsed)}>
              <Box display='flex' width={45} height={45}>{isCollapsed ? (<></>) : (<Box ref={container}></Box>)}</Box>
              <img src={process.env.PUBLIC_URL + '/inf-logo-text.svg'} alt="inf-logo-text" width={100} height={50} style={{ marginLeft: '4px', userSelect: "none", cursor: "pointer" }} onClick={() => window.innerWidth <= 650 ? (<></>) : (setIsCollapsed(!isCollapsed))} />
            </Box>
          )}
          {/* Portfolio button */}
          {
            !isCollapsed ? (
              <Box sx={{ padding: '10px' }}>
                <Box onClick={() => navigate(`/projects/${siteId}`)} border={1} sx={{ borderColor: '#343434', width: '100%', borderRadius: '10px', display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
                  <Item
                    title="Portfolio"
                    to={`/projects/${siteId}`}
                    icon={<BusinessIcon sx={{ width: '20.56px', height: '20.56px' }} />}
                    color='white'
                    selected={selected}
                    setSelected={setSelected}
                    isCollapsed={isCollapsed}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ padding: '3px', marginTop: '8px', marginBottom: '8px' }}>
                <Box onClick={() => navigate(`/projects/${siteId}`)} border={1} sx={{ borderColor: '#343434', width: '100%', borderRadius: '10px' }}>
                  <Item
                    title='Portfolio'
                    to={`/projects/${siteId}`}
                    icon={<BusinessIcon />}
                    color='white'
                    selected={selected}
                    setSelected={setSelected}
                    isCollapsed={isCollapsed}
                  />
                </Box>
              </Box>
            )
          }
          <Divider />
          {/* Return to building overview button */}
          {
            // Dont want to see button on these pages
            pathname === `/projects` || pathname === `/projects/${siteId}` || pathname === `/projects/${siteId}/buildingoverview` ? (null) : (
              <>
                <Item
                  title='Gebouw overzicht'
                  to={`/projects/${siteId}/buildingoverview`}
                  icon={<UndoOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  isCollapsed={isCollapsed}
                />
                <Divider />
              </>
            )
          }
          {/* Ems */}
          {
            userTabWhitelist.includes('ems/profile') && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('ems/')) ? (
              <SubMenu title='EMS' icon={<BoltIcon />} defaultOpen={true}>
                {
                  userTabWhitelist.includes('ems/profile') && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('ems/profile')) ? (
                    <Item
                      title="Energieprofiel"
                      to={`/projects/${siteId}/ems/profile?dateRange=${selectedDate[0].dateString}`}
                      icon={<EnergySavingsLeaf />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (
                    null
                  )
                }
                {
                  userTabWhitelist.includes('ems/benchmark') && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('ems/benchmark')) ? (
                    <Item
                      title="Benchmark"
                      to={`/projects/${siteId}/ems/benchmark?dateRange=${selectedYear[0].dateString}`}
                      icon={<Leaderboard />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (
                    null
                  )
                }
                {
                  userTabWhitelist.includes('ems/baseload') && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('ems/baseload')) ? (
                    <Item
                      title="Basislast"
                      to={`/projects/${siteId}/ems/baseload?dateRange=${selectedDate[0].dateString}`}
                      icon={<SpeedIcon />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (
                    null
                  )
                }
                {
                  userTabWhitelist.includes('ems/performancetrend') && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('ems/performancetrend')) ? (
                    <Item
                      title="Prestatietrend"
                      to={`/projects/${siteId}/ems/performancetrend?dateRange=${selectedDate[0].dateString}`}
                      icon={<TrendingUpIcon />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (
                    null
                  )
                }
                {
                  userTabWhitelist.includes('ems/energytempcurve') && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('ems/energytempcurve')) ? (
                    <Item
                      title="Stooklijn"
                      to={`/projects/${siteId}/ems/energytempcurve?dateRange=${selectedDate[0].dateString}`}
                      icon={<ThermostatIcon />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (
                    null
                  )
                }
              </SubMenu>
            ) : (<></>)
          }
          {userTabWhitelist.some(item => item.includes('ems/')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('ems/')) ? (<Divider variant="middle" />) : (<></>)}
          {/* Comfort */}
          {
            userTabWhitelist.some(item => item.includes('comfort/')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('comfort/')) ? (
                <SubMenu title='Comfort' icon={<Engineering />} defaultOpen={true}>
                  {
                    userTabWhitelist.includes('comfort/building') & projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('comfort/building')) ? (
                      <Item
                        title="3D Weergave"
                        to={`/projects/${siteId}/comfort/building`}
                        icon={<ApartmentIcon />}
                        selected={selected}
                        setSelected={setSelected}
                        isCollapsed={isCollapsed}
                      />
                    ) : (
                      null
                    )
                  }
                  {
                    userTabWhitelist.includes('comfort/table') && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('comfort/table')) ? (
                      <Item
                        title="Tabel Data"
                        to={`/projects/${siteId}/comfort/table`}
                        icon={<BackupTableIcon />}
                        selected={selected}
                        setSelected={setSelected}
                        isCollapsed={isCollapsed}
                      />
                    ) : (
                      null
                    )
                  }
                  {/* nav button for 3D View for locations of components */}
                  {
                    siteId !== undefined && siteId === "p:gem_westland_glazenstad:r:20fa2c89-368ef325" ? (
                      <Item
                        title="3D Componenten"
                        to={`/projects/${siteId}/comfort/componentlocation`}
                        icon={<ApartmentIcon />}
                        selected={selected}
                        setSelected={setSelected}
                        isCollapsed={isCollapsed}
                      />
                    ) : (
                      null
                    )
                  }
                </SubMenu>
            ) : (
              <></>
            )
          }
          {userTabWhitelist.some(item => item.includes('comfort/')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('comfort/')) ? (<Divider variant="middle" />) : (<></>)}
          {/* C&M */}
          {
            userTabWhitelist.some(item => item.includes('cm/')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('cm/')) ? (
              <SubMenu title='C&M' icon={<ThermostatIcon />} defaultOpen={true}>
                {
                  userTabWhitelist.some(item => item.includes('cm/outage')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('cm/outage')) ? (
                    <Item
                      title="Storing & Alarmen"
                      to={`/projects/${siteId}/template`}
                      icon={<UpcomingIcon />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (
                    null
                  )
                }
                {
                  userTabWhitelist.some(item => item.includes('cm/orders')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('cm/orders')) ? (
                    <Item
                      title="Werkorders"
                      to={`/projects/${siteId}/template`}
                      icon={<RoomServiceIcon />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (
                    null
                  )
                }
                {
                  userTabWhitelist.some(item => item.includes('cm/mjop')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('cm/mjop')) ? (
                    <Item
                      title="MJOP"
                      to={`/projects/${siteId}/template`}
                      icon={<NextPlanIcon />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (
                    null
                  )
                }
              </SubMenu>
            ) : (
              <></>
            )
          }
          {userTabWhitelist.some(item => item.includes('cm/')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('cm/')) ? (<Divider variant="middle" />) : (<></>)}
          {/* Cloud Control */}
          {
            userTabWhitelist.some(item => item.includes('cloudcontrol/')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('cloudcontrol/')) ? (
              <SubMenu title='Cloud Control' icon={<CloudDoneIcon />} defaultOpen={true}>
                {
                  userTabWhitelist.some(item => item.includes('cloudcontrol/template')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('cloudcontrol/template')) ? (
                    <Item
                      title="Template"
                      to={`/projects/${siteId}/template`}
                      icon={<ArticleIcon />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (null)
                }
                {
                  userTabWhitelist.some(item => item.includes('cloudcontrol/controlpanel')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('cloudcontrol/controlpanel')) ? (
                    <Item
                      title="Control panel"
                      to={`/projects/${siteId}/template`}
                      icon={<WysiwygIcon />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (null)
                }
                {
                  userTabWhitelist.some(item => item.includes('cloudcontrol/controlperformance')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('cloudcontrol/controlperformance')) ? (
                    <Item
                      title="Control performance"
                      to={`/projects/${siteId}/template`}
                      icon={<AlignVerticalCenterIcon />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (null)
                }
                {
                  userTabWhitelist.some(item => item.includes('cloudcontrol/updates')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('cloudcontrol/updates')) ? (
                    <Item
                      title="Updates"
                      to={`/projects/${siteId}/template`}
                      icon={<NotificationsIcon />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (null)
                }
              </SubMenu>
            ) : (
              <></>
            )
          }
          {userTabWhitelist.some(item => item.includes('cloudcontrol/')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('cloudcontrol/')) ? (<Divider variant="middle" />) : (<></>)}
          {
            userTabWhitelist.some(item => item.includes('tools/')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('tools/')) ? (
              <SubMenu title='Tools' icon={<HandymanIcon />} defaultOpen={true}>
                {
                  userTabWhitelist.some(item => item.includes('tools/maintenance')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('tools/maintenance')) ? (
                    <Item
                      title="Onderhoudsmodule"
                      to={`/projects/${siteId}/maintenance?dateRange=${selectedDate[0].dateString}`}
                      icon={<Engineering />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (null)
                }
                {
                  userTabWhitelist.some(item => item.includes('tools/arcs')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('tools/arcs')) ? (
                    <Item
                      title="Arcs"
                      to={`/projects/${siteId}/arcs`}
                      icon={<ReceiptOutlinedIcon />}
                      selected={selected}
                      setSelected={setSelected}
                      isCollapsed={isCollapsed}
                    />
                  ) : (null)
                }
              </SubMenu>
            ) : (
              <></>
            )
          }
          {userTabWhitelist.some(item => item.includes('tools/')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('tools/')) ? (<Divider variant="middle" />) : (<></>)}
          {/* Live table pages */}
          {userTabWhitelist.some(item => item.includes('dynamictable/')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('dynamictable/')) ? (
            liveTablePages.length > 0 ? (
              <SubMenu title="Live Data" icon={<BackupTableIcon />} defaultOpen={true}>
                {
                  liveTablePages.map((page, key) => {
                    if(userTabWhitelist.some(item => item.includes(page["infTablePageName"]))) {
                      return (
                        <Item
                          title={page["infTablePageName"].replaceAll("_", " ")}
                          to={`/projects/${siteId}/comfort/liveTable/${page["infTablePageName"]}/${page['id']['val']}/${page['infPageRefreshTime']['val']}`}
                          icon={<BackupTableIcon />}
                          selected={selected}
                          setSelected={setSelected}
                          isCollapsed={isCollapsed}
                          key={key}
                        />
                      )
                    } else {
                      return null
                    }
                  })
                }
              </SubMenu>
            ) : (<></>)
          ) : (
            <></>
          )
          }
          {liveTablePages.length !== 0 && userTabWhitelist.some(item => item.includes('dynamictable/')) && projectTabWhitelist.some(item => item['infModuleSettingsKey'].includes('dynamictable/')) ? (<Divider variant="middle" />) : (<></>)}
          {
            window.innerWidth <= 650 ? (<></>) : (
              isCollapsed ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ position: 'absolute', bottom: '0px' }}>
                    <IconButton onClick={() => setIsCollapsed(!isCollapsed)} aria-label="open-sidebar">
                      <KeyboardDoubleArrowRightIcon style={{ color: colors.grey[100] }} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                undefined
              )
            )
          }
          {
            window.innerWidth <= 650 || isCollapsed ? (<></>) : (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ position: 'absolute', bottom: '0px' }}>
                  <IconButton onClick={() => setIsCollapsed(!isCollapsed)} aria-label="close-sidebar">
                    <KeyboardDoubleArrowLeftIcon style={{ color: colors.grey[100] }} />
                  </IconButton>
                </Box>
              </Box>
            )
          }
          {
            userTabWhitelist.length === 0 || projectTabWhitelist.length === 0 ? (
              <Typography textAlign='center' sx={{ marginTop: '164px', fontWeight: 'bold' }}>Geen modules beschikbaar</Typography>
            ) : (
              <></>
            )
          }
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
